import { useState } from "react";
import AWS from "aws-sdk";
import {
  awsID,
  awsBucketName,
  awsSECRET,
} from "../pages/utilities/axios-config";

export const useS3Upload = (options = {}) => {
  const { acl = "public-read", onUploadComplete, onUploadError } = options;

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedUrl, setUploadedUrl] = useState(null);
  const [error, setError] = useState(null);

  /**
   * Upload a file to S3
   * @param {File} file - The file to upload
   * @returns {Promise<string|null>} - The uploaded file URL or null if failed
   */
  const uploadFile = async (file) => {
    if (!file) {
      setError("No file provided");
      return null;
    }

    setLoading(true);
    setProgress(0);
    setError(null);
    setUploadedUrl(null);

    try {
      const s3 = new AWS.S3({
        accessKeyId: awsID,
        secretAccessKey: awsSECRET,
      });

      const fileKey = `${new Date().getTime()}_${file.name}`;
      const params = {
        Bucket: awsBucketName,
        Key: fileKey,
        Body: file,
        ContentType: file.type,
        ACL: acl,
      };

      const upload = s3.upload(params);

      // Track upload progress
      upload.on("httpUploadProgress", (progressEvent) => {
        const percentCompleted = Math.round(
          (progressEvent.loaded / progressEvent.total) * 100
        );
        setProgress(percentCompleted);
      });

      const data = await upload.promise();

      const photoUrl = data.Location;
      setUploadedUrl(photoUrl);

      if (onUploadComplete) {
        onUploadComplete(photoUrl, file);
      }

      return photoUrl;
    } catch (err) {
      const errorMessage = err.message || "Upload failed";
      setError(errorMessage);

      if (onUploadError) {
        onUploadError(err, file);
      }

      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Reset the hook state
   */
  const reset = () => {
    setLoading(false);
    setProgress(0);
    setUploadedUrl(null);
    setError(null);
  };

  return {
    uploadFile,
    loading,
    progress,
    uploadedUrl,
    error,
    reset,
  };
};

export const useS3UploadMultipleFiles = (options = {}) => {
  const { acl = "public-read", onUploadComplete, onUploadError } = options;

  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadedUrls, setUploadedUrls] = useState([]);
  const [error, setError] = useState(null);

  const uploadFiles = async (files) => {
    if (!files || files.length === 0) {
      setError("No files provided");
      return [];
    }

    setLoading(true);
    setProgress(0);
    setError(null);
    setUploadedUrls([]);

    try {
      const s3 = new AWS.S3({
        accessKeyId: awsID,
        secretAccessKey: awsSECRET,
      });

  

      const uploadPromises = files.map(async (file) => {
        const params = {
          Bucket: awsBucketName,
          Key: `${new Date().getTime()}_${file.name}`,
          Body: file,
          ContentType: file.type,
          ACL: acl,
        };

        try {
          const data = await s3.upload(params).promise();
          console.log(`File uploaded successfully: ${data.Location}`);
          return data;
        } catch (err) {
          console.error(`Error uploading ${file.name}:`, err);
          throw err; // Re-throw to propagate the error
        }
      });

      const data = await Promise.all(uploadPromises);

      const photoUrls = data.map((item) => item.Location);
      console.log("multiple files uploaded", photoUrls);
      setUploadedUrls(photoUrls);

      if (onUploadComplete) {
        onUploadComplete(photoUrls, files);
      }

      return photoUrls;
    } catch (err) {
      const errorMessage = err.message || "Upload failed";
      console.log("multiple files upload error", errorMessage);
      setError(errorMessage);

      if (onUploadError) {
        onUploadError(err, files);
      }

      return null;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setLoading(false);
    setProgress(0);
    setUploadedUrls([]);
    setError(null);
  };

  return {
    uploadFiles,
    loading,
    progress,
    uploadedUrls,
    error,
    reset,
  };
};
